/**
 * Global date picker — the one styled date input for the whole app.
 *
 * Custom-rendered (button trigger + calendar popover) rather than a native
 * <input type="date">, so the calendar carries KC3 styling instead of the OS
 * default: 3px radius, same trigger height/elevation as Select, indigo
 * selected day, Today/Clear shortcuts.
 *
 *   <DatePicker
 *     value={fromDate}              // 'YYYY-MM-DD' or ''
 *     onChange={setFromDate}
 *     ariaLabel="From date"
 *     size="sm"                     // compact filter bars
 *   />
 *
 * Internally renders a visually-hidden native <input type="date"> carrying
 * the same value — the semantic source of truth for assistive tech, forms,
 * and tests (type/change events). The styled button + calendar is the
 * visual surface for mouse and touch.
 *
 * Keyboard: opens on ↓/Enter/Space, arrows move day, PageUp/PageDown change
 * month, Enter/Space select, Esc close.
 */
import { useEffect, useLayoutEffect, useRef, useState, type JSX } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { CONTROL_SIZE, POPOVER_CLASSES, TRIGGER_CLASSES, popoverSide, type ControlSize } from './control-styles.js'

interface DatePickerProps {
  /** ISO 'YYYY-MM-DD' or ''. */
  value: string
  onChange: (value: string) => void
  ariaLabel: string
  disabled?: boolean
  size?: ControlSize
  /** Small caption rendered inside the trigger before the date ('From', 'To'). */
  label?: string
  /** Strip the trigger's own ring/shadow — for embedding inside another
   *  bordered container (e.g. a prev/next day stepper). */
  bare?: boolean
  /** Extra classes for the trigger wrapper — width overrides ('w-40'). */
  className?: string
  placeholder?: string
  min?: string
  max?: string
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const

const pad2 = (n: number): string => String(n).padStart(2, '0')
const fmtDate = (y: number, m: number, d: number): string => `${y}-${pad2(m + 1)}-${pad2(d)}`

function parseDate(v: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (!m) return null
  return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) }
}

function today(): { y: number; m: number; d: number } {
  const t = new Date()
  return { y: t.getFullYear(), m: t.getMonth(), d: t.getDate() }
}

const monthName = (y: number, m: number): string =>
  new Intl.DateTimeFormat('en', { month: 'long' }).format(new Date(y, m, 1))

/** 'YYYY-MM-DD' → '14 Oct 2026' for the trigger face (hidden input keeps ISO). */
const displayDate = (iso: string): string => {
  const p = parseDate(iso)
  if (!p) return iso
  return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(p.y, p.m, p.d),
  )
}

export default function DatePicker({
  value,
  onChange,
  ariaLabel,
  disabled = false,
  size = 'md',
  label,
  bare = false,
  className = '',
  placeholder = 'Select date',
  min,
  max,
}: DatePickerProps): JSX.Element {
  const s = CONTROL_SIZE[size]
  const parsed = parseDate(value)
  const [open, setOpen] = useState(false)
  const init = parsed ?? today()
  const [viewY, setViewY] = useState(init.y)
  const [viewM, setViewM] = useState(init.m)
  const [focusDate, setFocusDate] = useState<string | null>(null)
  const [alignRight, setAlignRight] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)

  // Flip to right-anchored when the popover would overflow the viewport edge.
  useLayoutEffect(() => {
    if (!open) return
    const r = popRef.current?.getBoundingClientRect()
    if (r) setAlignRight(r.right > window.innerWidth - 8)
  }, [open])

  // Click-away close.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Move DOM focus to the focused day cell while open.
  useEffect(() => {
    if (!open || focusDate === null) return
    gridRef.current
      ?.querySelector<HTMLButtonElement>(`[data-date="${focusDate}"]`)
      ?.focus()
  }, [open, focusDate])

  const clampDay = (y: number, m: number, d: number): string => {
    const dim = new Date(y, m + 1, 0).getDate()
    return fmtDate(y, m, Math.min(d, dim))
  }

  const openCalendar = (): void => {
    const base = parsed ?? today()
    setViewY(base.y)
    setViewM(base.m)
    setFocusDate(fmtDate(base.y, base.m, base.d))
    setOpen(true)
  }

  const choose = (iso: string): void => {
    onChange(iso)
    setOpen(false)
  }

  const moveFocus = (days: number): void => {
    const base = focusDate ? parseDate(focusDate) : (parsed ?? today())
    if (!base) return
    const t = new Date(base.y, base.m, base.d + days)
    setFocusDate(fmtDate(t.getFullYear(), t.getMonth(), t.getDate()))
    if (t.getMonth() !== viewM || t.getFullYear() !== viewY) {
      setViewY(t.getFullYear())
      setViewM(t.getMonth())
    }
  }

  const navMonth = (delta: number): void => {
    const t = new Date(viewY, viewM + delta, 1)
    setViewY(t.getFullYear())
    setViewM(t.getMonth())
    if (focusDate) setFocusDate(clampDay(t.getFullYear(), t.getMonth(), parseDate(focusDate)?.d ?? 1))
  }

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (disabled) return
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openCalendar()
      }
      return
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        break
      case 'ArrowLeft':
        e.preventDefault()
        moveFocus(-1)
        break
      case 'ArrowRight':
        e.preventDefault()
        moveFocus(1)
        break
      case 'ArrowUp':
        e.preventDefault()
        moveFocus(-7)
        break
      case 'ArrowDown':
        e.preventDefault()
        moveFocus(7)
        break
      case 'PageUp':
        e.preventDefault()
        navMonth(-1)
        break
      case 'PageDown':
        e.preventDefault()
        navMonth(1)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (focusDate) choose(focusDate)
        break
      case 'Tab':
        setOpen(false)
        break
    }
  }

  const outOfRange = (iso: string): boolean =>
    (min !== undefined && iso < min) || (max !== undefined && iso > max)

  // Build the 6-row day grid (Monday start), leading blanks first.
  const firstWeekday = (new Date(viewY, viewM, 1).getDay() + 6) % 7
  const daysInMonth = new Date(viewY, viewM + 1, 0).getDate()
  const todayIso = fmtDate(today().y, today().m, today().d)
  const cells: (string | null)[] = [
    ...Array<string | null>(firstWeekday).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => fmtDate(viewY, viewM, i + 1)),
  ]

  return (
    <div ref={rootRef} className={`group relative ${className}`}>
      <input
        type="date"
        aria-label={ariaLabel}
        className="sr-only"
        tabIndex={-1}
        disabled={disabled}
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openCalendar())}
        onKeyDown={onKeyDown}
        className={`${bare
          ? 'w-full appearance-none rounded-[3px] bg-transparent text-left font-medium text-slate-700 transition-colors focus:outline-none disabled:cursor-not-allowed disabled:text-slate-400'
          : TRIGGER_CLASSES} ${s.trigger} ${s.padLeft}`}
      >
        <span className="flex min-w-0 items-baseline gap-1.5">
          {label && (
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              {label}
            </span>
          )}
          <span className={`truncate ${value === '' ? 'text-slate-400' : ''}`}>
            {value !== '' ? displayDate(value) : placeholder}
          </span>
        </span>
      </button>
      <CalendarDays
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 transition-colors duration-150 group-hover:text-slate-600 ${open ? 'text-indigo-500' : ''} ${s.icon}`}
        strokeWidth={2}
      />

      {open && (
        <div
          ref={popRef}
          role="dialog"
          aria-label={ariaLabel}
          className={`${POPOVER_CLASSES} ${popoverSide(alignRight)} w-max p-3`}
          onKeyDown={onKeyDown}
        >
          {/* Month header */}
          <div className="mb-2 flex items-center justify-between gap-2">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => navMonth(-1)}
              className="rounded-[3px] p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={2.25} />
            </button>
            <span className={`font-semibold text-slate-800 ${s.popoverText}`}>
              {monthName(viewY, viewM)} {viewY}
            </span>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => navMonth(1)}
              className="rounded-[3px] p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
            >
              <ChevronRight className="h-4 w-4" strokeWidth={2.25} />
            </button>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-0.5">
            {WEEKDAYS.map((w) => (
              <span key={w} className="flex h-6 w-8 items-center justify-center text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {w}
              </span>
            ))}
          </div>

          {/* Day grid */}
          <div ref={gridRef} role="grid" className="grid grid-cols-7 gap-0.5">
            {cells.map((iso, i) =>
              iso === null ? (
                <span key={`b${i}`} className={s.dayCell} />
              ) : (
                <button
                  key={iso}
                  type="button"
                  role="gridcell"
                  data-date={iso}
                  aria-selected={iso === value}
                  aria-label={iso}
                  disabled={outOfRange(iso)}
                  tabIndex={-1}
                  onClick={() => choose(iso)}
                  className={`flex items-center justify-center rounded-[3px] transition-colors ${s.dayCell} ${
                    iso === value
                      ? 'bg-indigo-600 font-semibold text-white'
                      : iso === focusDate
                        ? 'bg-indigo-50 text-indigo-700'
                        : iso === todayIso
                          ? 'font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200'
                          : 'text-slate-600 hover:bg-slate-100'
                  } disabled:cursor-not-allowed disabled:text-slate-300`}
                >
                  {Number(iso.slice(8))}
                </button>
              ),
            )}
          </div>

          {/* Footer */}
          <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
            <button
              type="button"
              onClick={() => { choose('') }}
              className={`rounded-[3px] px-2 py-1 font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 ${s.popoverText}`}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => choose(todayIso)}
              className={`rounded-[3px] px-2 py-1 font-medium text-indigo-600 transition-colors hover:bg-indigo-50 ${s.popoverText}`}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
